import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity, CalendarDays, CheckSquare, Clock, Euro, FolderKanban, FolderOpen,
  KeyRound, LayoutDashboard, LogOut, Menu, Plus, Search, Settings, User, Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { UserAvatar } from "./ui-bits";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import { SearchDialog } from "./SearchDialog";
import { NotificationsMenu } from "./NotificationsMenu";
import { ProjectForm } from "./ProjectForm";
import { VideoCallButton } from "./VideoCallButton";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
  { to: "/projekte", label: "Projekte", icon: FolderKanban, testid: "sidebar-nav-projekte" },
  { to: "/aufgaben", label: "Aufgaben", icon: CheckSquare, testid: "sidebar-nav-aufgaben" },
  { to: "/kunden", label: "Kunden", icon: Users, testid: "sidebar-nav-kunden" },
  { to: "/dateien", label: "Dateien", icon: FolderOpen, testid: "sidebar-nav-dateien" },
  { to: "/zugaenge", label: "Zugänge", icon: KeyRound, testid: "sidebar-nav-zugaenge" },
  { to: "/zahlungen", label: "Zahlungen", icon: Euro, testid: "sidebar-nav-zahlungen" },
  { to: "/zeiterfassung", label: "Zeiterfassung", icon: Clock, testid: "sidebar-nav-zeiterfassung" },
  { to: "/aktivitaet", label: "Aktivität", icon: Activity, testid: "sidebar-nav-aktivitaet" },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "sidebar-nav-kalender" },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testid: "sidebar-nav-einstellungen", ownerOnly: true },
];

function NavItems({ user, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => !n.ownerOnly || user.role === "owner").map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          data-testid={n.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 ${
              isActive
                ? "bg-white/[0.07] text-white shadow-[inset_2px_0_0_#B88A4A]"
                : "text-[#A3AED0] hover:bg-white/[0.04] hover:text-white"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px] shrink-0 opacity-80 group-hover:opacity-100" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openProjectForm = async () => {
    try {
      const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
      setClients(c.data);
      setUsers(u.data);
      setProjectFormOpen(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[#11152F] md:flex" data-testid="sidebar">
        <div className="flex items-center gap-3 px-5 pb-6 pt-6">
          <img src="/assets/logo-white.png" alt="Manuela Gassner" className="h-9 w-auto" data-testid="sidebar-logo" />
        </div>
        <div className="mx-5 mb-4 h-px bg-white/[0.07]" />
        <div className="flex-1 overflow-y-auto pb-4">
          <NavItems user={user} />
        </div>
        <div className="border-t border-white/[0.07] p-4">
          <div className="flex items-center gap-3">
            <UserAvatar name={user.name} color={user.color} avatar={user.avatar} size={9} testid="sidebar-user-avatar" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{user.name}</p>
              <p className="truncate text-xs text-[#A3AED0]">{user.title}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#1D225C]/10 bg-[#F7F6F2]/85 backdrop-blur-md md:pl-64" data-testid="app-header">
        <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5 text-[#1D225C]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 bg-[#11152F] p-0 pt-6">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="px-5 pb-5">
                <img src="/assets/logo-white.png" alt="Manuela Gassner" className="h-9 w-auto" />
              </div>
              <NavItems user={user} onNavigate={() => setMenuOpen(false)} />
            </SheetContent>
          </Sheet>

          <button
            data-testid="global-search-button"
            onClick={() => setSearchOpen(true)}
            className="flex h-10 flex-1 items-center gap-2.5 rounded-xl border border-[#1D225C]/10 bg-white px-3.5 text-sm text-[#777] shadow-sm transition-colors hover:border-[#B88A4A]/50 sm:max-w-xs"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Suche…</span>
            <kbd className="hidden rounded-md border border-[#1D225C]/10 bg-[#F7F6F2] px-1.5 py-0.5 text-[10px] font-semibold text-[#1D225C]/60 sm:inline">
              ⌘K
            </kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <Button
              data-testid="quick-new-project-button"
              onClick={openProjectForm}
              className="hidden h-10 rounded-xl bg-[#B88A4A] px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#a67a3f] sm:flex"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Neues Projekt
            </Button>
            <VideoCallButton testid="team-call-button" label="Anruf" className="hidden sm:flex" />
            <NotificationsMenu />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-button" className="rounded-full ring-[#B88A4A]/0 transition-shadow hover:ring-2 hover:ring-[#B88A4A]/40">
                  <UserAvatar name={user.name} color={user.color} avatar={user.avatar} size={9} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 rounded-xl">
                <DropdownMenuLabel>
                  <p className="text-sm font-semibold text-[#1D225C]">{user.name}</p>
                  <p className="text-xs font-normal text-[#777]">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-menu-profile" onClick={() => navigate("/profil")}>
                  <User className="mr-2 h-4 w-4" /> Profil & Passwort
                </DropdownMenuItem>
                {user.role === "owner" && (
                  <DropdownMenuItem data-testid="profile-menu-settings" onClick={() => navigate("/einstellungen")}>
                    <Settings className="mr-2 h-4 w-4" /> Einstellungen
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-menu-logout" onClick={doLogout} className="text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" /> Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-6 sm:px-6 md:pl-[17.5rem] md:pr-6 md:pb-10" data-testid="main-content">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-white/10 bg-[#11152F] pb-[env(safe-area-inset-bottom)] md:hidden" data-testid="mobile-bottom-nav">
        {[
          { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "mobile-nav-dashboard" },
          { to: "/projekte", label: "Projekte", icon: FolderKanban, testid: "mobile-nav-projekte" },
          { to: "/aufgaben", label: "Aufgaben", icon: CheckSquare, testid: "mobile-nav-aufgaben" },
          { to: "/zahlungen", label: "Zahlungen", icon: Euro, testid: "mobile-nav-zahlungen" },
        ].map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${isActive ? "text-[#B88A4A]" : "text-[#A3AED0]"}`
            }
          >
            <n.icon className="h-5 w-5" />
            {n.label}
          </NavLink>
        ))}
        <button
          data-testid="mobile-nav-menu"
          onClick={() => setMenuOpen(true)}
          className="flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium text-[#A3AED0]"
        >
          <Menu className="h-5 w-5" />
          Menü
        </button>
      </nav>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <ProjectForm
        open={projectFormOpen}
        onOpenChange={setProjectFormOpen}
        clients={clients}
        users={users}
        onSaved={(p) => navigate(`/projekte/${p.id}`)}
      />
    </div>
  );
}
